"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var auth0_js_1 = require("auth0-js");
var es6Promise = tslib_1.__importStar(require("es6-promise"));
var cookies = tslib_1.__importStar(require("js-cookie"));
var lodash_defaults_1 = tslib_1.__importDefault(require("lodash.defaults"));
var v4_1 = tslib_1.__importDefault(require("uuid/v4"));
var decorators_1 = require("./decorators");
var helpers = tslib_1.__importStar(require("./helpers"));
/*
 * IE doesn't support Promises. This is ironically the one "modern" feature
 * the Typescript ES5 output relies on.
 */
es6Promise.polyfill();
require("isomorphic-fetch");
var APPMETADATAPATH = 'https://identity.ingrooves.com/claims/app_metadata';
var THEMENAME = 'theme';
var SSOCOOKIENAME = 'sso';
var AUTH0AUTHERRMSG = 'Empty response';
var IDCLIENTAUTHERRMSG = 'Not logged in';
function toAuth0Options(opts, state, audience) {
    var responseType = opts.responseType, scope = opts.scope, redirectUri = opts.redirectUri, connection = opts.connection, clientID = opts.clientID, domain = opts.domain;
    /*
     * Pick out props relevant to Auth0.JS and add state+audience.
     *  - state should be provided for all auth calls
     *  - audience should _not_ be provided to authorize, as we don't want
     *    our SSO session limited to one audience.
     */
    return {
        audience: audience,
        clientID: clientID,
        connection: connection,
        domain: domain,
        redirectUri: redirectUri,
        responseType: responseType,
        scope: scope,
        state: state,
    };
}
var IdentityClient = /** @class */ (function () {
    function IdentityClient(options) {
        this.renewInterval = helpers.minutes(10);
        this.intervals = {};
        this.defaults = {
            connection: 'Username-Password-Authentication',
            disablePolling: false,
            forceAuthorize: false,
            redirectUri: window.location.origin,
            responseType: 'token id_token',
            scope: 'openid profile email',
        };
        for (var _i = 0, _a = ['domain', 'clientID']; _i < _a.length; _i++) {
            var prop = _a[_i];
            if (!(prop in options)) {
                throw new Error("IdentityClient: " + prop + " is not defined.");
            }
        }
        if ('logoutPath' in options) {
            this.logoutPath = options.logoutPath;
        }
        this.settings = lodash_defaults_1.default({}, options, this.defaults);
        this.api = new auth0_js_1.WebAuth(toAuth0Options(this.settings));
        // ex: ".ingrooves.com" - we want the sso key to apply to subdomains
        cookies.defaults.domain = "." + window.location.hostname.split('.').slice(-2).join('.');
    }
    Object.defineProperty(IdentityClient.prototype, "isLogoutPath", {
        get: function () {
            var _a;
            return typeof this.logoutPath === 'string'
                && ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === this.logoutPath;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IdentityClient.prototype, "cookie", {
        get: function () {
            return cookies.get(SSOCOOKIENAME);
        },
        set: function (value) {
            if (value) {
                cookies.set(SSOCOOKIENAME, value);
            }
            else {
                cookies.remove(SSOCOOKIENAME);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IdentityClient.prototype, "theme", {
        get: function () {
            return cookies.get(THEMENAME);
        },
        set: function (value) {
            if (value) {
                cookies.set(THEMENAME, value);
            }
            else {
                cookies.remove(THEMENAME);
            }
        },
        enumerable: true,
        configurable: true
    });
    IdentityClient.prototype.logout = function (serverSideLogoutUrl) {
        if (serverSideLogoutUrl === void 0) { serverSideLogoutUrl = ''; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, clientID, returnTo, e_1;
            var _this = this;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        helpers.popRedirectState();
                        return [4 /*yield*/, helpers.flushQueue()];
                    case 1:
                        _b.sent();
                        return [4 /*yield*/, helpers.clearNonces()];
                    case 2:
                        _b.sent();
                        this.cookie = null;
                        this.theme = null;
                        helpers.clearSessions();
                        Object.keys(this.intervals).forEach(function (key) {
                            clearInterval(_this.intervals[key]);
                        });
                        _a = this.settings, clientID = _a.clientID, returnTo = _a.redirectUri;
                        if (!serverSideLogoutUrl) return [3 /*break*/, 3];
                        // leave clearing the SSO session up to the server
                        window.location.href = serverSideLogoutUrl;
                        return [3 /*break*/, 6];
                    case 3:
                        _b.trys.push([3, 5, , 6]);
                        return [4 /*yield*/, helpers.invokeWithPromiseCallback('logout', this.api, { returnTo: returnTo, clientID: clientID })];
                    case 4: return [2 /*return*/, _b.sent()];
                    case 5:
                        e_1 = _b.sent();
                        // redirect on error otherwise we're letting the UI hang
                        window.location.href = returnTo;
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    IdentityClient.prototype.getSession = function (audience, opts) {
        if (opts === void 0) { opts = {}; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var ssoKey, sessionOpts, token, storedState, result, scope, accessToken, idToken, state, expiresAt, userTheme, tokenResult, err_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isLogoutPath) {
                            return [2 /*return*/, helpers.flushQueue()];
                        }
                        ssoKey = this.cookie;
                        sessionOpts = lodash_defaults_1.default(tslib_1.__assign({}, opts), this.settings);
                        if (!sessionOpts.forceAuthorize) {
                            token = helpers.getToken(audience, ssoKey);
                            if (token && !helpers.isExpired(token.expiresAt)) {
                                return [2 /*return*/, token];
                            }
                        }
                        storedState = helpers.popRedirectState();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, helpers.invokeWithPromiseCallback('checkSession', this.api, tslib_1.__assign({ prompt: 'none' }, toAuth0Options(sessionOpts, storedState, audience)))];
                    case 2:
                        result = _a.sent();
                        scope = result.scope, accessToken = result.accessToken, idToken = result.idTokenPayload, state = result.state;
                        expiresAt = helpers.calculateExpiry(result.expiresIn);
                        if (!ssoKey) {
                            ssoKey = v4_1.default();
                            this.cookie = ssoKey;
                        }
                        userTheme = idToken[APPMETADATAPATH][THEMENAME];
                        if (userTheme) {
                            this.theme = userTheme;
                        }
                        tokenResult = helpers.setSession({
                            accessToken: accessToken,
                            expiresAt: expiresAt,
                            idToken: idToken,
                            scope: scope,
                        }, audience, ssoKey);
                        helpers.handleRedirect(state, this.logoutPath);
                        clearInterval(this.intervals[audience]);
                        if (!this.settings.disablePolling) {
                            this.intervals[audience] = setInterval(this.renew.bind(this, audience, opts), this.renewInterval);
                        }
                        return [2 /*return*/, tokenResult];
                    case 3:
                        err_1 = _a.sent();
                        if (err_1.message !== AUTH0AUTHERRMSG) {
                            throw err_1;
                        }
                        else {
                            throw new Error(IDCLIENTAUTHERRMSG);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    IdentityClient.prototype.renew = function (audience, opts) {
        if (opts === void 0) { opts = {}; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var err_2, sessionOpts, state;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isLogoutPath) {
                            return [2 /*return*/, helpers.flushQueue()];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.getSession(audience, opts)];
                    case 2: return [2 /*return*/, _a.sent()];
                    case 3:
                        err_2 = _a.sent();
                        if (err_2.message !== IDCLIENTAUTHERRMSG) {
                            throw err_2;
                        }
                        return [3 /*break*/, 4];
                    case 4:
                        sessionOpts = lodash_defaults_1.default(tslib_1.__assign({}, opts), this.settings);
                        return [4 /*yield*/, helpers.flushQueue()];
                    case 5:
                        _a.sent();
                        state = helpers.saveRedirectState(opts.redirectPath);
                        return [4 /*yield*/, helpers.invokeWithPromiseCallback('authorize', this.api, toAuth0Options(sessionOpts, state))];
                    case 6: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    IdentityClient.prototype.getToken = function (audience) {
        var token = helpers.getToken(audience, this.cookie);
        return token && token.accessToken;
    };
    IdentityClient.prototype.requestPasswordReset = function (email) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var connection;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        connection = this.settings.connection;
                        return [4 /*yield*/, helpers.invokeWithPromiseCallback('changePassword', this.api, {
                                connection: connection,
                                email: email,
                            })];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    tslib_1.__decorate([
        decorators_1.readonly
    ], IdentityClient.prototype, "settings", void 0);
    tslib_1.__decorate([
        decorators_1.readonly
    ], IdentityClient.prototype, "api", void 0);
    return IdentityClient;
}());
exports.IdentityClient = IdentityClient;
